import React, { useState, useEffect } from 'react';

interface OrientationManagerProps {
  children: React.ReactNode;
}

/**
 * MobileReportOrientationManager
 * 
 * Detects device orientation and shows a message on mobile devices in portrait mode
 * to encourage users to rotate to landscape for better report viewing
 */
const MobileReportOrientationManager: React.FC<OrientationManagerProps> = ({ children }) => {
  const [isPortrait, setIsPortrait] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Function to check orientation and device
    const checkOrientationAndDevice = () => {
      const portrait = window.innerHeight > window.innerWidth;
      setIsPortrait(portrait);
      
      // Check if device is mobile (simple check, can be enhanced)
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };

    // Initial check
    checkOrientationAndDevice();
    
    // Listen for orientation changes
    window.addEventListener('resize', checkOrientationAndDevice);
    
    // Clean up listener on unmount
    return () => {
      window.removeEventListener('resize', checkOrientationAndDevice);
    };
  }, []);

  // Only show orientation message on mobile devices in portrait mode
  if (isMobile && isPortrait) {
    return (
      <div className="bg-white p-6 font-sans">
        <div className="flex flex-col items-center justify-center p-6 bg-blue-50 rounded-lg text-center shadow-lg">
          {/* ROI Logo */}
          <img 
            src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png" 
            alt="ROI Exteriors Logo" 
            className="h-16 mb-6" 
          />
            
          {/* Device rotation animation */}
          <div className="relative mb-8">
            <div className="h-20 w-32 border-2 border-blue-600 rounded-lg relative overflow-hidden rotate-90 transform transition-all duration-1000 bg-white shadow-md">
              <div className="absolute inset-1 rounded bg-blue-100 flex items-center justify-center">
                <div className="w-3 h-3 absolute top-1 left-1/2 transform -translate-x-1/2 rounded-full bg-blue-200"></div>
                <div className="w-10 h-1 rounded-full bg-blue-200"></div>
              </div>
            </div>
            
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <svg className="w-24 h-24 text-blue-500 animate-spin-slow" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Please Rotate Your Device</h2>
          <p className="text-gray-600 mb-4 max-w-md">
            These marketing reports are designed to be viewed in landscape orientation on mobile devices.
            Please rotate your device horizontally for the best viewing experience.
          </p>
          
          <div className="border-t border-b border-gray-200 py-3 my-3 w-full">
            <p className="text-blue-600 font-medium">
              Detailed marketing data is easier to view in landscape mode.
            </p>
          </div>
          
          <p className="text-sm text-gray-500">
            Alternatively, you can access these reports on a desktop or tablet device for an enhanced experience.
          </p>
        </div>
      </div>
    );
  }

  // Otherwise, render the children
  return <>{children}</>;
};

export default MobileReportOrientationManager;